import React, { useMemo, useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Badge, ButtonGroup } from "react-bootstrap";
import swal from "sweetalert";

import Tables from "jsx/components/CitoPlus/components/tables";
import SearchInput from "jsx/components/CitoPlus/components/input_search";
import {
  CitoPlusLinkIconDanger,
  CitoPlusLinkIconPrimary,
  LinkPrimary,
} from "jsx/components/CitoPlus/components/CitoPlusLink";
import { SpinnerCitoPlus } from "jsx/components/CitoPlus/components/loadingLogo";
import {
  notifyError,
  notifySuccess,
} from "jsx/components/CitoPlus/components/CitoPlusToastr";
import NoImagePlaceholder from "jsx/components/CitoPlus/components/NoImagePlaceholder";

import axiosInstance from "../../../../../services/AxiosInstance";
import { getDefaultParams } from "../../../../../services/DefaultParamets";

import "../../../table/FilteringTable/filtering.css";

const DIAS_SEMANA = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const getTipoReservaBadge = (tipo, maxHoras) => {
  if (tipo === "hora") {
    return (
      <Badge bg="info">
        Por hora ({maxHoras}h máx)
      </Badge>
    );
  }
  return <Badge bg="primary">Por día</Badge>;
};

const getReservableBadge = (reservable) => {
  return reservable ? (
    <Badge bg="success">Reservable</Badge>
  ) : (
    <Badge bg="secondary">No reservable</Badge>
  );
};

export const ZonasSociales = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [sortField, setSortField] = useState("");
  const [order, setOrder] = useState("asc");

  const [nextUrl, setNextUrl] = useState(null);
  const [previousUrl, setPreviousUrl] = useState(null);

  const [defaultParams] = useState(getDefaultParams);

  const URL = "/api/v3/zonas-sociales/zonas/";

  const fetchUrl = useCallback(
    async (pageIndex = 1, search = "", ordering = "") => {
      setLoading(true);

      try {
        if (pageIndex < 1 || pageIndex > totalPages) {
          notifyError("Página no encontrada");
          return;
        }

        const params = {
          ...defaultParams,
          page: pageIndex,
          search,
        };

        if (ordering) {
          params.ordering = ordering;
        }

        const response = await axiosInstance.get(URL, { params });
        setNextUrl(response.data.next);
        setPreviousUrl(response.data.previous);
        setTotalPages(response.data.total_pages);
        setTotalRecords(response.data.count);
        setCurrentPage(response.data.current_page);
        setData(response.data.results);
      } catch (error) {
        console.error("Error fetching data:", error);
        notifyError("Error al cargar las zonas sociales");
      } finally {
        setLoading(false);
      }
    },
    [URL, defaultParams, totalPages]
  );

  const handleDeleteZonaSocial = useCallback(
    async (zonaUuid) => {
      const willDelete = await swal({
        title: "¿Deseas eliminar esta zona social?",
        text: "Esta acción no se puede deshacer.",
        icon: "warning",
        buttons: ["Cancelar", "Eliminar"],
        dangerMode: true,
      });

      if (!willDelete) {
        return;
      }

      try {
        await axiosInstance.delete(`${URL}${zonaUuid}/`);
        const orderingValue = sortField
          ? `${order === "desc" ? "-" : ""}${sortField}`
          : "";
        const nextPage =
          data.length === 1 && currentPage > 1
            ? currentPage - 1
            : currentPage;

        notifySuccess("Zona social eliminada correctamente");
        await fetchUrl(nextPage, searchValue, orderingValue);
      } catch (error) {
        console.error("Error eliminando zona social:", error);
        notifyError("Error al eliminar la zona social");
      }
    },
    [URL, currentPage, data.length, fetchUrl, order, searchValue, sortField]
  );

  const formatDiasOperacion = (dias) => {
    if (!dias || dias.length === 0) return "-";
    if (dias.length === 7) return "Todos los días";
    if (JSON.stringify(dias.sort()) === JSON.stringify([0, 1, 2, 3, 4]))
      return "Lun - Vie";
    return dias.map((d) => DIAS_SEMANA[d]).join(", ");
  };

  const formatHorario = (inicio, fin) => {
    if (!inicio || !fin) return "-";
    return `${inicio} - ${fin}`;
  };

  const columns = useMemo(
    () => [
      {
        id: "imagen",
        Header: "",
        disableFilters: true,
        disableSortBy: true,
        accessor: (row) => {
          return row.imagen_url ? (
            <img
              src={row.imagen_url}
              alt={row.nombre}
              className="rounded"
              style={{
                width: "60px",
                height: "60px",
                objectFit: "cover",
              }}
            />
          ) : (
            <NoImagePlaceholder alt={row.nombre} height="60px" />
          );
        },
      },
      {
        id: "nombre",
        Header: "Nombre",
        accessor: (row) => (
          <div>
            <div className="fw-semibold">{row.nombre}</div>
            <small className="text-muted">
              {row.recursos_count || 0} recursos
            </small>
          </div>
        ),
        disableFilters: true,
      },
      {
        id: "tipo_reserva",
        Header: "Tipo",
        accessor: (row) =>
          getTipoReservaBadge(row.tipo_reserva, row.tiempo_maximo_reserva),
        disableFilters: true,
        disableSortBy: true,
      },
      {
        id: "horario",
        Header: "Horario",
        accessor: (row) => (
          <div>
            <div>{formatHorario(row.horario_inicio, row.horario_fin)}</div>
            <small className="text-muted">
              {formatDiasOperacion(row.dias_operacion)}
            </small>
          </div>
        ),
        disableFilters: true,
        disableSortBy: true,
      },
      {
        id: "capacidad",
        Header: "Capacidad",
        accessor: (row) => (
          <div>
            <span className="text-muted">Min:</span> {row.capacidad_min} |{" "}
            <span className="text-muted">Max:</span> {row.capacidad_max}
          </div>
        ),
        disableFilters: true,
        disableSortBy: true,
      },
      {
        id: "max_inmuebles",
        Header: "Concurrencia",
        accessor: (row) => {
          const max = row.max_inmuebles_simultaneos;
          if (max === 0) {
            return <Badge bg="secondary">Ilimitado</Badge>;
          }
          if (max === 1) {
            return <Badge bg="warning">1 inmueble</Badge>;
          }
          return <Badge bg="info">{max} inmuebles</Badge>;
        },
        disableFilters: true,
        disableSortBy: true,
      },
      {
        id: "estado",
        Header: "Estado",
        accessor: (row) => getReservableBadge(row.reservable),
        disableFilters: true,
        disableSortBy: true,
      },
      {
        Header: "Acciones",
        disableFilters: true,
        disableSortBy: true,
        accessor: (row) => (
          <ButtonGroup aria-label="Acciones zona social">
            <CitoPlusLinkIconPrimary
              title="Ver detalles"
              to={`/zona-social/detalle/${row.uuid}`}
            >
              <i className="fas fa-eye"></i>
            </CitoPlusLinkIconPrimary>
            <CitoPlusLinkIconPrimary
              title="Editar"
              to={`/zona-social/editar/${row.uuid}`}
            >
              <i className="fas fa-pencil-alt"></i>
            </CitoPlusLinkIconPrimary>
            <CitoPlusLinkIconDanger
              title="Eliminar"
              to="#"
              onClick={() => handleDeleteZonaSocial(row.uuid)}
            >
              <i className="fas fa-trash"></i>
            </CitoPlusLinkIconDanger>
          </ButtonGroup>
        ),
      },
    ],
    [handleDeleteZonaSocial]
  );

  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const debouncedFetchUrl = useCallback(
    debounce((pageIndex, search) => {
      fetchUrl(pageIndex, search);
    }, 500),
    [fetchUrl]
  );

  useEffect(() => {
    fetchUrl(1);
  }, [fetchUrl]);

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setLoading(true);
    debouncedFetchUrl(1, value);
  };

  const handleEnterPress = () => {
    fetchUrl(1, searchValue);
  };

  const handleClearClick = () => {
    handleSearchChange("");
  };

  const handleSortingChange = (column) => {
    const { id } = column;
    const newOrder =
      sortField === id ? (order === "asc" ? "desc" : "asc") : "asc";
    const orderingValue = id ? `${newOrder === "desc" ? "-" : ""}${id}` : "";

    setSortField(id);
    setOrder(newOrder);
    fetchUrl(1, searchValue, orderingValue);
  };

  return (
    <>
      <section className="card">
        <header className="card-header">
          <div className="d-flex flex-wrap align-items-center gap-2 w-100">
            <div className="d-flex flex-column me-auto flex-grow-1">
              <h2 className="card-title mb-0">Zonas Sociales</h2>
              <div className="text-muted small">
                Administra las zonas sociales del conjunto.
              </div>
            </div>
            <div className="d-flex gap-2 ms-auto">
              <LinkPrimary to="/zonas-sociales/crear/">
                <i className="fas fa-plus me-2"></i>
                Nueva Zona
              </LinkPrimary>
            </div>
          </div>
        </header>

        <div className="card-body">
          <div className="row g-3 mb-4">
            <div className="col-lg-4 col-md-6">
              <SearchInput
                value={searchValue}
                onSearchChange={handleSearchChange}
                onEnterPress={handleEnterPress}
                onClearClick={handleClearClick}
              />
            </div>
          </div>

          {loading ? (
            <div className="py-5 text-center">
              <SpinnerCitoPlus text="Cargando zonas sociales..." />
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-building fa-3x text-muted mb-3"></i>
              <h5>No hay zonas sociales</h5>
              <p className="text-muted">
                Crea una nueva zona social para comenzar.
              </p>
              <LinkPrimary to="/zonas-sociales/crear/">
                <i className="fas fa-plus me-2"></i>
                Crear Zona Social
              </LinkPrimary>
            </div>
          ) : (
            <Tables
              columns={columns}
              data={data}
              totalPages={totalPages}
              totalRecords={totalRecords}
              currentPage={currentPage}
              fetchUrl={fetchUrl}
              handleSortingChange={handleSortingChange}
              nextUrl={nextUrl}
              previousUrl={previousUrl}
              sortField={sortField}
              order={order}
            />
          )}
        </div>
      </section>
    </>
  );
};

export default ZonasSociales;
